import React from "react";
import { format } from "date-fns";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

function formatDateRangeLabel(startDate, endDate, placeholder, displayFormat) {
  if (startDate && endDate) {
    return format(startDate, displayFormat) + " - " + format(endDate, displayFormat);
  }

  if (startDate) {
    return format(startDate, displayFormat) + " -";
  }

  return placeholder;
}

export default function MerchantDateRangePickerField({
  disabled,
  endDate,
  maxDate,
  minDate,
  onChange,
  placeholder,
  rangeDisplayFormat,
  startDate,
}) {
  const rootRef = React.useRef(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [focusedRange, setFocusedRange] = React.useState([0, 0]);
  const displayFormat = rangeDisplayFormat || "MM/dd";
  const fallbackDate = startDate || endDate || new Date();
  const valueLabel = formatDateRangeLabel(
    startDate,
    endDate,
    placeholder || "Select date range",
    displayFormat,
  );

  React.useEffect(
    function bindOutsideClick() {
      if (!isOpen) {
        return undefined;
      }

      function handleDocumentMouseDown(event) {
        if (rootRef.current && !rootRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      }

      function handleDocumentKeyDown(event) {
        if (event.key === "Escape") {
          setIsOpen(false);
        }
      }

      document.addEventListener("mousedown", handleDocumentMouseDown);
      document.addEventListener("keydown", handleDocumentKeyDown);

      return function cleanup() {
        document.removeEventListener("mousedown", handleDocumentMouseDown);
        document.removeEventListener("keydown", handleDocumentKeyDown);
      };
    },
    [isOpen],
  );

  React.useEffect(
    function closeWhenDisabled() {
      if (disabled) {
        setIsOpen(false);
      }
    },
    [disabled],
  );

  function handleRangeChange(ranges) {
    const selection = ranges && ranges.selection;

    if (!selection || !onChange) {
      return;
    }

    onChange([selection.startDate, selection.endDate]);
  }

  return (
    <div className="merchant-date-range-picker" ref={rootRef}>
      <button
        className="merchant-date-picker-trigger"
        type="button"
        onClick={function togglePicker() {
          setIsOpen(function updateOpenState(previousState) {
            return !previousState;
          });
        }}
        disabled={disabled}
      >
        <span className="merchant-date-picker-trigger-label">{valueLabel}</span>
        <span className="merchant-date-picker-trigger-icon" aria-hidden="true">
          ▾
        </span>
      </button>

      {isOpen ? (
        <div className="merchant-date-range-picker-panel">
          <DateRange
            dateDisplayFormat="MM/dd/yyyy"
            direction="horizontal"
            editableDateInputs
            focusedRange={focusedRange}
            maxDate={maxDate || undefined}
            minDate={minDate || undefined}
            months={2}
            moveRangeOnFirstSelection={false}
            onChange={handleRangeChange}
            onRangeFocusChange={setFocusedRange}
            rangeColors={["#1e3a8a"]}
            ranges={[
              {
                startDate: startDate || fallbackDate,
                endDate: endDate || startDate || fallbackDate,
                key: "selection",
              },
            ]}
            showMonthAndYearPickers
          />
          <div className="merchant-date-range-picker-footer">
            <button
              className="merchant-button merchant-button-primary merchant-button-small"
              type="button"
              onClick={function closePicker() {
                setIsOpen(false);
              }}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
